import { BlockTool } from '@editorjs/editorjs/types/tools/block-tool';
import { API, BlockToolData, HTMLPasteEvent, ToolConfig } from '@editorjs/editorjs';

import BlockSettings from './block-settings/blockSettings';
import { getCaretPosition, setCaretPosition } from '../../../services/editorJS/utils';

/*
eslint-disable @typescript-eslint/no-explicit-any,
import/no-unresolved,
@typescript-eslint/no-unused-vars,
max-len,
no-case-declarations,
lines-between-class-members
*/

type ConstructorParams = {
  api: API;
  config: ToolConfig;
  data: BlockToolData;
};

// @ts-ignore
class Paragraph implements BlockTool {
  protected readonly api: API;
  protected _CSS: any;
  protected _placeholder: string;
  protected _data: BlockToolData;
  protected _element: HTMLElement;
  protected _preserveBlank: boolean;

  static get DEFAULT_PLACEHOLDER() {
    return '';
  }

  /**
   * Render plugin`s main Element and fill it with saved data
   *
   * @param {object} params - constructor params
   * @param {ParagraphData} params.data - previously saved data
   * @param {ParagraphConfig} params.config - user config for Tool
   * @param {object} params.api - editor.js api
   */
  constructor({ data, config, api }: ConstructorParams) {
    this.api = api;

    this._CSS = {
      block: this.api.styles.block,
      wrapper: 'ce-paragraph',
      shadowCaret: 'cdx-shadow-caret',
    };
    this.onKeyUp = this.onKeyUp.bind(this);

    /**
     * Placeholder for paragraph if it is first Block
     * @type {string}
     */
    this._placeholder = config?.placeholder ? config?.placeholder : Paragraph.DEFAULT_PLACEHOLDER;
    this._data = {};
    this._element = this.drawView();
    this._preserveBlank = config?.preserveBlank !== undefined ? config?.preserveBlank : false;

    this.data = data;
  }

  /**
   * Check if text content is empty and set empty string to inner html.
   * We need this because some browsers (e.g. Safari) insert <br> into empty contenteditanle elements
   *
   * @param {KeyboardEvent} e - key up event
   */
  onKeyUp(e: KeyboardEvent) {
    if (e.code === 'Delete') {
      this.onDelete();
    }

    if (e.code === 'Backspace') {
      this.onBackspace();
    }

    if (e.code !== 'Backspace' && e.code !== 'Delete') {
      return;
    }

    const { textContent } = this._element;

    if (textContent === '') {
      this._element.innerHTML = '';
    }
  }

  onKeyDown(e: KeyboardEvent) {
    if (e.code === 'Backspace') {
      this.onBackspace();
    }
  }

  /**
   * Create Tool's view
   * @return {HTMLElement}
   * @private
   */
  drawView() {
    let div = document.createElement('DIV');

    div.classList.add(this._CSS.wrapper, this._CSS.block);
    div.contentEditable = 'true';
    div.dataset.placeholder = this.api.i18n.t(this._placeholder);

    div.addEventListener('keyup', this.onKeyUp);

    return div;
  }

  /**
   * Return Tool's view
   * @returns {HTMLDivElement}
   * @public
   */
  render() {
    return this._element;
  }

  /**
   * Method that specified how to merge two Text blocks.
   * Called by Editor.js by backspace at the beginning of the Block
   * @param {ParagraphData} data
   * @public
   */
  merge(data: any) {
    let newData = {
      text: this.data.text + data.text,
    };

    this.data = newData;
  }

  /**
   * Extract Tool's data from the view
   * @param {HTMLDivElement} toolsContent - Paragraph tools rendered view
   * @returns {ParagraphData} - saved data
   * @public
   */
  save(toolsContent: any) {
    return {
      text: toolsContent.innerHTML,
    };
  }

  /**
   * On paste callback fired from Editor.
   *
   * @param {PasteEvent} event - event with pasted data
   */
  onPaste(event: HTMLPasteEvent) {
    const data = {
      text: event.detail.data.innerHTML,
    };

    this.data = data;
  }

  /**
   * Enable Conversion Toolbar. Paragraph can be converted to/from other tools
   */
  // static get conversionConfig() {
  //   return {
  //     export: 'text', // to convert Paragraph to other block, use 'text' property of saved data
  //     import: 'text', // to covert other block's exported string to Paragraph, fill 'text' property of tool data
  //   };
  // }

  /**
   * Sanitizer rules
   */
  static get sanitize() {
    return {
      text: {
        br: true,
      },
    };
  }

  /**
   * Get current Tools`s data
   * @returns {ParagraphData} Current data
   * @private
   */
  get data() {
    let text = this._element.innerHTML;

    this._data.text = text;

    return this._data;
  }

  /**
   * Store data in plugin:
   * - at the this._data property
   * - at the HTML
   *
   * @param {ParagraphData} data — data to set
   * @private
   */
  set data(data) {
    this._data = data || {};

    this._element.innerHTML = this._data.text?.trim() || '';
  }

  /**
   * Used by Editor paste handling API.
   * Provides configuration to handle P tags.
   *
   * @returns {{tags: string[]}}
   */
  static get pasteConfig() {
    return {
      tags: ['P'],
    };
  }

  /**
   * Icon and title for displaying at the Toolbox
   *
   * @return {{icon: string, title: string}}
   */
  // static get toolbox() {
  //   return {
  //     icon: 'T',
  //     title: 'Text',
  //   };
  // }

  validate() {
    return true;
  }

  renderSettings() {
    const blockSettings = new BlockSettings({ api: this.api });

    return blockSettings.render();
  }

  protected async onDelete() {
    try {
      const blockLength = this._element.textContent?.length;
      const blockLengthWithTrim = this._element.textContent?.trim()?.length;
      const range = getCaretPosition(this._element);
      const caretPosition = (range?.toString() || '').length;

      if (blockLength === range?.endOffset || blockLengthWithTrim === caretPosition) {
        const currentBlockIndex = this.api.blocks.getCurrentBlockIndex();
        const nextBlockIndex = currentBlockIndex + 1;
        if (this.api.blocks.getBlocksCount() > nextBlockIndex) {
          const nextBlock = this.api.blocks.getBlockByIndex(nextBlockIndex);
          this.createShadow();
          if (nextBlock.name === 'paragraph') {
            const paragraphContent = nextBlock.holder.querySelector(`.${this._CSS.wrapper}`);
            if (paragraphContent) {
              this.merge({
                text: paragraphContent.innerHTML,
              });
            }
          }
          setCaretPosition(this._element, caretPosition)
            .then(() => this.restoreCaret());
          this.api.blocks.delete(nextBlockIndex);
        }
      }
    } catch (e) {
      console.error(e);
    }
  }

  protected async onBackspace() {
    try {
      const range = getCaretPosition(this._element);
      const caretPosition = (range?.toString() || '').length;

      if (caretPosition === 0) {
        // delete previous block, previous block === current block index
        const currentBlockIndex = this.api.blocks.getCurrentBlockIndex();

        if (currentBlockIndex >= 0) {
          const currentBlock = this.api.blocks.getBlockByIndex(currentBlockIndex);

          if (currentBlock.name !== 'paragraph' && !currentBlock.isEmpty) {
            this.api.blocks.delete(currentBlockIndex);
            this.api.caret.setToBlock(currentBlockIndex);
          }
        }
      }
    } catch (e) {
      console.error(e);
    }
  }

  createShadow(): void {
    const shadowCaret = document.createElement('span');

    shadowCaret.classList.add(this._CSS.shadowCaret);
    this._element.insertAdjacentElement('beforeend', shadowCaret);
  }

  restoreCaret(): void {
    const shadowCaret = this._element.querySelector(`.${this._CSS.shadowCaret}`);
    if (!shadowCaret) {
      return;
    }

    /**
     * After we set the caret to the required place
     * we need to clear shadow caret
     *
     * - make new range
     * - select shadowed span
     * - use extractContent to remove it from DOM
     */

    this.api.selection.expandToTag(shadowCaret as HTMLElement);
    const newRange = document.createRange();

    newRange.selectNode(shadowCaret);
    newRange.extractContents();
  }
}

export default Paragraph;
